import React, { useState, useCallback } from 'react';
import { Text, Stack, Button, Overlay } from '@moda/om';
import { reportError } from '../../../lib/reportError';
import { useDeletePaymentMethodMutation } from '../../../hooks/usePaymentMethods';
import { LoadingBalls } from '../../LoadingBalls';
import { ModalOverlay } from '../../Modal';
import './PaymentMethodDeleteModal.scss';

type Props = {
  paymentMethodId: string;
  onClose: () => void;
};

enum Mode {
  Resting,
  Deleting,
  Failed
}

export const PaymentMethodDeleteModal: React.FC<Props> = ({ paymentMethodId, onClose }) => {
  const [mutate] = useDeletePaymentMethodMutation(paymentMethodId);
  const [mode, setMode] = useState(Mode.Resting);

  const handleDelete = useCallback(async () => {
    setMode(Mode.Deleting);
    try {
      await mutate();
      onClose();
    } catch (error) {
      reportError(error);
      setMode(Mode.Failed);
    }
  }, [setMode, mutate, onClose]);

  return (
    <>
      <ModalOverlay className="PaymentMethodDeleteModal" onClose={onClose}>
        <Stack className="PaymentMethodDeleteModal__delete-modal-content" space={4}>
          <Text treatment="h4" family="serif">
            Do you want to delete this payment method permanently?
          </Text>
          {mode === Mode.Failed && (
            <Text color="code-red">An error occurred. Please retry later.</Text>
          )}

          <Button className="PaymentMethodDeleteModal__button" onClick={handleDelete}>
            Yes, Delete it!
          </Button>
          <Button className="PaymentMethodDeleteModal__button" onClick={onClose}>
            No, Keep it
          </Button>
        </Stack>
      </ModalOverlay>

      <Overlay show={mode === Mode.Deleting}>
        <LoadingBalls />
        <Text treatment="eyebrow">Deleting</Text>
      </Overlay>
    </>
  );
};
